import { inject, Injectable, signal } from '@angular/core';
import { ADYEN_RESUME_CONSTANTS } from '../constants/adyen-resume.constants';
import { ADYEN_RESULT_CODE } from '../constants/adyen-result-code.constants';
import { AdyenHostedResultResponse, AdyenPaymentDetails } from '../models/adyen.model';
import {
  AdyenResumeContext,
  AdyenResumeFailure,
  AdyenResumeRecord,
  AdyenResumeReturn,
  AdyenReturnParams,
} from '../models/adyen-resume.model';
import { ADYEN_HOSTED_CONSTANTS } from '../constants/adyen-hosted.constants';
import { AdyenLoaderService } from './adyen-loader.service';
import { AdyenPaymentHoldService } from './adyen-payment-hold.service';
import {
  adyenResumeContextMatches,
  adyenReturnMatchesRecord,
  isAdyenResumeRecord,
  isAdyenResumeRecordExpired,
  nextAdyenPollDelay,
  readAdyenReturnParams,
  stripAdyenReturnParams,
} from './adyen-resume.util';
import { LoggerService } from './logger.service';
import { PaymentService } from './payment.service';

/**
 * Finishes an Adyen payment after the shopper had to leave the page, and hands the host the same
 * opaque result an in-page payment produces - so the booking goes through `/book` exactly as it
 * would have without the detour, and `/book` still verifies the payment with Adyen itself.
 *
 * Read at app level (widget.component), not in the Drop-in component: that one renders only at the
 * payment step, which a returning page never reaches by itself.
 *
 * sessionStorage, keyed per partner: a parked payment must not outlive the tab, must not be read by
 * another partner's widget on the same origin, and is untouched by the localStorage wipe the widget
 * runs on every load.
 */
@Injectable({ providedIn: 'root' })
export class AdyenResumeService {

  private adyenLoaderService = inject(AdyenLoaderService);
  private adyenPaymentHoldService = inject(AdyenPaymentHoldService);
  private paymentService = inject(PaymentService);

  /**
   * A resume is finishing a payment. The Drop-in component must not mint a second payable session
   * for the same booking meanwhile - it waits for this to go false.
   */
  private readonly resumingState = signal(false);
  readonly resuming = this.resumingState.asReadonly();

  /**
   * Sessions a resume was already handed out for in this document. Two widgets of one partner on a
   * page share the record: only the first may finish it, or both would book the same payment.
   */
  private readonly takenSessionIds = new Set<string>();

  /** @returns false when the browser refused to store it (then nothing can be resumed) */
  write(record: AdyenResumeRecord): boolean {
    try {
      window.sessionStorage.setItem(this.storageKey(record.partnerId), JSON.stringify(record));
      return true;
    } catch (error) {
      LoggerService.error(error);
      return false;
    }
  }

  /** The parked payment for this partner, or null when there is none, or it is corrupt or expired. */
  read(partnerId: number | undefined): AdyenResumeRecord | null {
    if (!partnerId) {
      return null;
    }
    try {
      const raw = window.sessionStorage.getItem(this.storageKey(partnerId));
      if (!raw) {
        return null;
      }
      const record: unknown = JSON.parse(raw);
      if (!isAdyenResumeRecord(record, partnerId) || isAdyenResumeRecordExpired(record, Date.now())) {
        this.clear(partnerId);
        return null;
      }
      return record;
    } catch (error) {
      LoggerService.error(error);
      return null;
    }
  }

  clear(partnerId: number | undefined): void {
    if (!partnerId) {
      return;
    }
    try {
      window.sessionStorage.removeItem(this.storageKey(partnerId));
    } catch (error) {
      LoggerService.error(error);
    }
  }

  /** Drops the record only while it is still this session's - a newer payment may have replaced it. */
  clearIfOwnedBy(partnerId: number | undefined, sessionId: string): void {
    if (this.read(partnerId)?.sessionId === sessionId) {
      this.clear(partnerId);
    }
  }

  /**
   * `/book` is being sent with this payment: from now on it is a made booking, never a payable one.
   * Called only once no guard can stop the request any more - a record marked for a request that
   * was never sent would tell the shopper a booking exists that does not.
   */
  markBookingSent(partnerId: number | undefined, sessionId: string | undefined): void {
    if (!sessionId) {
      return;
    }
    const record = this.read(partnerId);
    if (record?.sessionId === sessionId) {
      this.write({ ...record, bookingSent: true });
    }
  }

  /**
   * `/book` answered for a resumed payment, so the parked record has done its job. Only while no
   * answer arrived (the tab died mid-request) does it stay, and then says "already booked" instead of
   * taking a second payment for the same booking.
   */
  releaseIfBookingSent(partnerId: number | undefined): void {
    if (this.read(partnerId)?.bookingSent) {
      this.clear(partnerId);
    }
  }

  /**
   * Reads what a payment return put on the page URL and removes it with `history.replaceState`.
   *
   * Runs on load, before the partner is known, so Back or a reload cannot replay the return. Only a
   * return that is ours is removed: `adyen_hosted` (only the Calenso return page adds it), or any
   * return while this tab parked an Adyen payment. A partner page running its own Adyen checkout
   * beside the widget comes back with `redirectResult`/`sessionId` too, and must still find them.
   * Every `return_url` is built from a stripped URL anyway (cw-adyen), so what stays cannot travel.
   */
  captureReturnParams(): AdyenReturnParams | null {
    const params = readAdyenReturnParams(window.location.search);
    if (params && (params.hostedNonce || this.hasAnyRecord())) {
      try {
        const cleaned = stripAdyenReturnParams(window.location.href);
        if (cleaned !== window.location.href) {
          window.history.replaceState(window.history.state, '', cleaned);
        }
      } catch (error) {
        LoggerService.error(error);
      }
    }

    return params;
  }

  /**
   * The return on the URL, when it belongs to the payment this tab parked for this partner.
   *
   * Anything else - no record (another tab, cleared storage, another integration on the page), a
   * return for a different session, a booking already sent - is not resumed. The page must never
   * book on a `redirectResult` it cannot tie to the booking that was paid for.
   *
   * A matched return is kept in the record: the URL is already clean, so a reload (or a crashed tab)
   * before `/book` went out resumes from the record instead of stranding an authorised payment.
   *
   * A hosted record with no return at all is still handed out, as a recovery: the hosted page may
   * have been paid while this page was gone (Back from it, a reload while its window was open, a
   * return page with nowhere to redirect to). The recovery only reads the stored result.
   *
   * Handed out once per session in this document (see takenSessionIds).
   *
   * Never in the hosted checkout's own window: it was opened from the paying tab, so the browser gave
   * it a copy of that tab's sessionStorage, record included. When the window lost its link to the
   * tab, the return page sends it back to the widget - and resuming there would book the payment a
   * second time beside the paying tab, which settles it by polling.
   */
  takeReturn(partnerId: number | undefined, params: AdyenReturnParams | null): AdyenResumeReturn | null {
    const record = this.read(partnerId);
    if (!record || record.bookingSent || this.takenSessionIds.has(record.sessionId)) {
      return null;
    }
    if (record.kind === 'hosted' && this.isHostedCheckoutWindow()) {
      return null;
    }
    let resume: AdyenResumeReturn | null = null;
    if (!params) {
      if (record.returned) {
        resume = { record, redirectResult: record.returned.redirectResult };
      } else if (record.kind === 'hosted' && !!record.nonce && !!record.token) {
        resume = { record, recovered: true };
      }
    } else if (adyenReturnMatchesRecord(params, record)) {
      const redirectResult = record.kind === 'dropin' ? params.redirectResult : undefined;
      const returned: AdyenResumeRecord = { ...record, returned: { redirectResult } };
      this.write(returned);
      resume = { record: returned, redirectResult };
    }

    if (resume) {
      this.takenSessionIds.add(record.sessionId);
    }
    return resume;
  }

  /**
   * Completes the payment the record parked and resolves with the handles `/book` takes.
   *
   * Rejects with an {@link AdyenResumeFailure}. Order matters: a held (Pending) payment for this
   * booking is honoured first, then the booking the host restored must be the one that was paid
   * for, and only then is anything sent to Adyen.
   *
   * @param current the booking the host restored, recomputed from its own state
   */
  complete(resume: AdyenResumeReturn, current: AdyenResumeContext): Promise<AdyenPaymentDetails> {
    const { record } = resume;
    this.resumingState.set(true);

    const held = this.adyenPaymentHoldService.heldReason(record.partnerId, record.holdScope);
    if (held) {
      this.clear(record.partnerId);
      return this.settle(Promise.reject(this.failure(held)));
    }

    if (!adyenResumeContextMatches(record, current)) {
      // Not this booking any more. The payment stays unbooked and the reconciliation job reverses it.
      LoggerService.warn('[Adyen] Resumed payment does not match the restored booking - not booked');
      this.clear(record.partnerId);
      return this.settle(Promise.reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.CONTEXT_CHANGED)));
    }

    const result = record.kind === 'dropin'
      ? this.completeDropin(record, resume.redirectResult ?? '')
      : this.completeHosted(record, !!resume.recovered);

    return this.settle(result.catch((failure: AdyenResumeFailure) => {
      this.clear(record.partnerId);
      throw failure;
    }));
  }

  /**
   * Adyen Web (6.x), Sessions flow, redirect return: a checkout created from the session id alone,
   * then `submitDetails` with the `redirectResult`. The SDK answers through the same result callbacks
   * an in-page payment uses.
   */
  private async completeDropin(record: AdyenResumeRecord, redirectResult: string): Promise<AdyenPaymentDetails> {
    const answered = record.returned?.sessionResult;
    if (answered) {
      // Adyen already answered these details before a reload: submitting them again is not needed.
      return { session_id: record.sessionId, session_result: answered };
    }
    if (!redirectResult) {
      throw this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
    }
    try {
      await this.adyenLoaderService.load(record.environment ?? 'test');
    } catch (error) {
      LoggerService.error(error);
      throw this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
    }
    const sdk = this.adyenLoaderService.getSdk();
    if (!sdk) {
      throw this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED);
    }

    return new Promise<AdyenPaymentDetails>((resolve, reject) => {
      const onResult = (result: { resultCode?: string; sessionResult?: string } | undefined) => {
        const code = result?.resultCode;
        if (code === ADYEN_RESULT_CODE.AUTHORISED && result?.sessionResult) {
          this.write({ ...record, returned: { redirectResult, sessionResult: result.sessionResult } });
          resolve({ session_id: record.sessionId, session_result: result.sessionResult });
        } else if (code === ADYEN_RESULT_CODE.PENDING || code === ADYEN_RESULT_CODE.RECEIVED) {
          // Money may still arrive: nothing may mint a second payable session for this booking.
          this.adyenPaymentHoldService.hold(record.partnerId, record.holdScope, ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.PENDING);
          reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.PENDING));
        } else if (code === ADYEN_RESULT_CODE.AUTHORISED) {
          // Without the sessionResult the backend cannot verify it, so it must never become a booking.
          reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED));
        } else {
          reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.REFUSED));
        }
      };

      sdk.AdyenCheckout({
        environment: record.environment,
        clientKey: record.clientKey,
        session: { id: record.sessionId },
        analytics: { enabled: false },
        onPaymentCompleted: onResult,
        onPaymentFailed: onResult,
        onError: (error: unknown) => {
          LoggerService.error(error);
          reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED));
        },
      }).then(checkout => {
        (checkout as { submitDetails: (data: unknown) => void }).submitDetails({ details: { redirectResult } });
      }).catch(error => {
        LoggerService.error(error);
        reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.UNEXPECTED));
      });
    });
  }

  /**
   * The hosted page came back in this tab. The Calenso return page stored the result; it is read with
   * the nonce AND the token only this tab was given. Read with a back-off, and again at once when the
   * tab becomes visible or focused - timers in a background tab are throttled.
   *
   * @param recovered no return came with this page: one read decides, and "no result" is silent
   */
  private completeHosted(record: AdyenResumeRecord, recovered: boolean): Promise<AdyenPaymentDetails> {
    const deadline = record.createdAt + ADYEN_RESUME_CONSTANTS.HOSTED_TTL_MS;

    return new Promise<AdyenPaymentDetails>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let delay: number | undefined;
      let inFlight = false;
      let done = false;

      const finish = (outcome: () => void): void => {
        done = true;
        clearTimeout(timer);
        window.removeEventListener('focus', pollNow);
        document.removeEventListener('visibilitychange', pollNow);
        outcome();
      };

      const schedule = (): void => {
        clearTimeout(timer);
        delay = nextAdyenPollDelay(delay);
        timer = setTimeout(poll, delay);
      };

      const poll = (): void => {
        if (done || inFlight) {
          return;
        }
        if (Date.now() >= deadline) {
          finish(() => reject(this.failure(ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.EXPIRED)));
          return;
        }
        inFlight = true;
        this.paymentService.getAdyenHostedResult(record.nonce as string, record.token as string).subscribe({
          next: (result: AdyenHostedResultResponse) => {
            inFlight = false;
            if (
              result?.status === ADYEN_HOSTED_CONSTANTS.RESULT_STATUS.RETURNED
              && result.session_id === record.sessionId
              && !!result.session_result
            ) {
              const sessionResult = result.session_result;
              finish(() => resolve({ session_id: record.sessionId, session_result: sessionResult }));
            } else if (recovered) {
              finish(() => reject(this.silentFailure()));
            } else if (!done) {
              schedule();
            }
          },
          error: (error: unknown) => {
            // A missed read is not a failed payment; the next one still settles it.
            inFlight = false;
            LoggerService.warn(error);
            if (recovered) {
              finish(() => reject(this.silentFailure()));
            } else if (!done) {
              schedule();
            }
          },
        });
      };

      const pollNow = (): void => {
        if (document.visibilityState === 'hidden') {
          return;
        }
        poll();
      };

      window.addEventListener('focus', pollNow);
      document.addEventListener('visibilitychange', pollNow);
      poll();
    });
  }

  private settle(result: Promise<AdyenPaymentDetails>): Promise<AdyenPaymentDetails> {
    return result.finally(() => this.resumingState.set(false));
  }

  private failure(messageKey: string): AdyenResumeFailure {
    return { messageKey };
  }

  private silentFailure(): AdyenResumeFailure {
    return { messageKey: ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.RESUME_UNAVAILABLE, silent: true };
  }

  /** This document runs in the window the hosted checkout was opened in (see takeReturn). */
  private isHostedCheckoutWindow(): boolean {
    try {
      return window.name === ADYEN_HOSTED_CONSTANTS.POPUP_NAME;
    } catch {
      return false;
    }
  }

  /** This tab parked an Adyen payment for some partner (the partner is not known yet on load). */
  private hasAnyRecord(): boolean {
    try {
      for (let i = 0; i < window.sessionStorage.length; i++) {
        if (window.sessionStorage.key(i)?.startsWith(ADYEN_RESUME_CONSTANTS.STORAGE_KEY_PREFIX)) {
          return true;
        }
      }
    } catch (error) {
      LoggerService.error(error);
    }
    return false;
  }

  private storageKey(partnerId: number): string {
    return `${ADYEN_RESUME_CONSTANTS.STORAGE_KEY_PREFIX}${partnerId}`;
  }
}
